import { useEffect, useRef, useState } from "react";
import { Star, ShieldCheck, FileCheck2, Camera, Mic, MessageSquare, X, CheckCircle2 } from "lucide-react";

/**
 * ReviewMediationAnimation — self-looping visual showing the lifecycle of an
 * unfair 1-star review from "posted" → "disputed with evidence" →
 * "mediation" → "removed", playing on a 10s cycle. Purely CSS-driven —
 * no motion library needed. Pauses when off-screen for perf.
 */
const CYCLE_MS = 10500;
const STEPS = [
  { at: 0,    label: "Unfair review posted" },
  { at: 2200, label: "Business disputes with evidence" },
  { at: 4800, label: "Mediation review" },
  { at: 7400, label: "Verdict: Removed" },
];

export default function ReviewMediationAnimation() {
  const [tick, setTick] = useState(0);
  const [active, setActive] = useState(true);
  const rootRef = useRef(null);

  // Pause the loop when the animation is off-screen — keeps the page snappy
  // and prevents janky reflows when the user isn't looking.
  useEffect(() => {
    if (!rootRef.current || typeof IntersectionObserver !== "function") return;
    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(rootRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const startAt = Date.now();
    const t = setInterval(() => {
      setTick((Date.now() - startAt) % CYCLE_MS);
    }, 60);
    return () => clearInterval(t);
  }, [active]);

  // Which step index is currently the "highlighted" one?
  const currentStep = STEPS.reduce((idx, s, i) => (tick >= s.at ? i : idx), 0);

  // Sub-state flags
  const showReview = tick >= 100;
  const showDispute = tick >= 2200;
  const showMediation = tick >= 4800;
  const showVerdict = tick >= 7400;
  const removed = tick >= 8200;

  return (
    <div
      ref={rootRef}
      className="relative rounded-2xl border border-[#F0C9C4] bg-white p-6 md:p-8 overflow-hidden"
      data-testid="review-mediation-animation"
    >
      {/* Ambient gradient wash — subtle red glow that pulses on step change */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#FDF3F1] via-transparent to-transparent transition-opacity duration-700"
        style={{ opacity: showMediation && !removed ? 1 : 0.5 }}
      />

      <div className="relative grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
        {/* LEFT — the review "card" that mutates through the states */}
        <div className="md:col-span-3">
          <div
            className={`relative rounded-xl border bg-white p-5 transition-all duration-700 ${
              removed
                ? "border-[#EAE3DE] bg-[#FAF5F2]/60 opacity-60"
                : showDispute
                  ? "border-amber-300 shadow-[0_10px_40px_-15px_rgba(179,30,30,0.28)]"
                  : "border-[#EAE3DE] shadow-[0_6px_20px_-10px_rgba(0,0,0,0.15)]"
            }`}
            style={{
              transform: showReview ? "translateY(0)" : "translateY(12px)",
              opacity: showReview ? 1 : 0,
              transitionProperty: "transform, opacity, background-color, border-color, box-shadow",
            }}
          >
            {/* Big red "REMOVED" stamp that rotates in when verdict lands */}
            {removed && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                data-testid="mediation-removed-stamp"
              >
                <div
                  className="border-4 border-[#B31E1E] text-[#B31E1E] font-black text-2xl md:text-3xl tracking-[0.3em] px-4 py-2 rounded"
                  style={{
                    transform: "rotate(-8deg)",
                    letterSpacing: "0.3em",
                    animation: "mediation-stamp 380ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
                  }}
                >
                  REMOVED
                </div>
              </div>
            )}

            {/* Author row */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#EAE3DE] text-[#8B7B73] flex items-center justify-center font-black text-xs shrink-0">
                RK
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-[14px] ${removed ? "line-through text-[#8B7B73]" : "text-[#1B1614]"}`}>
                  R. Kowalski
                </div>
                <div className="text-[11px] text-[#8B7B73]">Tue · verified customer</div>
              </div>
              {!removed && (
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={14}
                      className={s === 1 ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#EAE3DE]"}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Review body */}
            <p className={`mt-3 text-[13.5px] leading-relaxed transition-colors ${
              removed ? "line-through text-[#8B7B73]" : "text-[#3E2A26]"
            }`}>
              &ldquo;Waited 45 minutes and the service was terrible. Would not recommend. Total waste of time — worst experience ever.&rdquo;
            </p>

            {/* Dispute banner slides in */}
            {showDispute && !removed && (
              <div
                className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 text-[12px] text-amber-900"
                style={{ animation: "mediation-slide-in 400ms ease-out both" }}
                data-testid="mediation-dispute-banner"
              >
                <ShieldCheck size={13} className="shrink-0" />
                <span><strong>Business disputed</strong> — evidence attached, mediation requested.</span>
              </div>
            )}

            {/* Evidence chips slide up on the dispute step */}
            {showDispute && !removed && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mediation-evidence-chips">
                {[
                  { Icon: Camera, label: "Timestamped photo" },
                  { Icon: Mic, label: "Audio recording" },
                  { Icon: MessageSquare, label: "SMS thread" },
                  { Icon: FileCheck2, label: "Receipt PDF" },
                ].map(({ Icon, label }, i) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-white/70 px-2 py-0.5 text-[11px] font-mono text-amber-900"
                    style={{ animation: `mediation-slide-in 400ms ease-out ${(i * 90)}ms both` }}
                  >
                    <Icon size={10} /> {label}
                  </span>
                ))}
              </div>
            )}

            {/* Mediation review shimmer */}
            {showMediation && !showVerdict && (
              <div
                className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 flex items-center gap-2 text-[12px] text-blue-800 overflow-hidden relative"
                data-testid="mediation-review-banner"
              >
                <div className="relative w-3.5 h-3.5">
                  <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-60" />
                  <div className="absolute inset-0.5 rounded-full bg-blue-500" />
                </div>
                <span><strong>Mediation team reviewing</strong> — weighing evidence from both sides.</span>
                {/* Sweeping shimmer bar */}
                <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent" style={{ animation: "mediation-shimmer 1.6s linear infinite" }} />
              </div>
            )}

            {/* Verdict banner */}
            {showVerdict && (
              <div
                className={`mt-4 rounded-lg border px-3 py-2 flex items-center gap-2 text-[12px] transition-colors ${
                  removed ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-blue-200 bg-blue-50 text-blue-800"
                }`}
                style={{ animation: "mediation-slide-in 400ms ease-out both" }}
                data-testid="mediation-verdict-banner"
              >
                <CheckCircle2 size={13} className="shrink-0" />
                <span>
                  <strong>Verdict:</strong> Review removed. Star average restored.
                </span>
              </div>
            )}
          </div>

          {/* Star-average gauge — ticks up when the review is removed */}
          <div className="mt-5 flex items-center gap-3 text-[12px]" data-testid="mediation-avg-gauge">
            <span className="text-[#8B7B73] font-mono uppercase text-[10px] tracking-wider">Business rating</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={14}
                  className={s <= (removed ? 5 : 4) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#EAE3DE]"}
                />
              ))}
            </div>
            <span className={`font-mono font-bold transition-all duration-500 ${removed ? "text-emerald-700" : "text-[#3E2A26]"}`}>
              {removed ? "4.8 ↑" : "4.6"}
            </span>
            {removed && (
              <span
                className="text-[10px] rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 font-mono font-semibold"
                style={{ animation: "mediation-slide-in 500ms ease-out both" }}
              >
                +0.2
              </span>
            )}
          </div>
        </div>

        {/* RIGHT — vertical stepper */}
        <div className="md:col-span-2">
          <ol className="relative border-l-2 border-[#EAE3DE] pl-5 space-y-5" data-testid="mediation-stepper">
            {STEPS.map((s, i) => {
              const done = currentStep > i || (currentStep === i && i === STEPS.length - 1);
              const active = currentStep === i;
              return (
                <li key={s.label} data-testid={`mediation-step-${i}`}>
                  <span
                    className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 transition-all duration-500 ${
                      done
                        ? "bg-[#B31E1E] border-[#B31E1E]"
                        : active
                          ? "bg-[#F9E4E1] border-[#B31E1E] scale-125"
                          : "bg-white border-[#EAE3DE]"
                    }`}
                    style={{ transform: active ? "translateY(2px) scale(1.25)" : "translateY(2px) scale(1)" }}
                  />
                  <div className={`text-[10px] font-mono uppercase tracking-wider ${done || active ? "text-[#B31E1E]" : "text-[#8B7B73]"}`}>
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className={`text-[14px] font-semibold ${active || done ? "text-[#1B1614]" : "text-[#8B7B73]"}`}>
                    {s.label}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-6 rounded-lg bg-[#FAF5F2] border border-[#EAE3DE] p-3 text-[11px] leading-relaxed text-[#5C4E48]">
            <strong className="text-[#1B1614]">Fair mediation, both sides.</strong> Malicious or fabricated claims never survive to the star average — ratings you see on Servrevu actually reflect what happened.
          </div>
        </div>
      </div>

      {/* Keyframes — scoped via <style> so we don't pollute globals */}
      <style>{`
        @keyframes mediation-stamp {
          0% { opacity: 0; transform: rotate(-30deg) scale(1.8); }
          60% { opacity: 1; transform: rotate(-5deg) scale(0.95); }
          100% { opacity: 1; transform: rotate(-8deg) scale(1); }
        }
        @keyframes mediation-slide-in {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes mediation-shimmer {
          0% { transform: translateX(0); }
          100% { transform: translateX(500%); }
        }
      `}</style>
    </div>
  );
}
